import { useEffect, useRef, useSyncExternalStore } from 'react';

/**
 * "הגדרות קשורות" — shared state between the top-bar button, the side panel and the screens.
 *   openRelatedSettings()      open the panel (also Ctrl+,)
 *   useScreenSettings(items)   a screen offers its own settings (dialogs inside the screen) to the panel
 *   recent / return            last opened settings, and the "חזרה ל…" pill after leaving a screen
 */
export const OPEN_RELATED_EVENT = 'bchinot:open-related-settings';

export function openRelatedSettings() {
  window.dispatchEvent(new CustomEvent(OPEN_RELATED_EVENT));
}

/* ── settings that live inside the current screen ── */
let screenItems = [];
const listeners = new Set();
const emit = () => listeners.forEach((l) => l());
const subscribe = (l) => { listeners.add(l); return () => listeners.delete(l); };
const snapshot = () => screenItems;

/**
 * useScreenSettings([{ key, title, description, icon, onClick }]) — shown first in the panel
 * ("בתוך המסך הזה") while the screen is mounted. onClick runs after the panel closes.
 */
export function useScreenSettings(items) {
  const ref = useRef(items);
  ref.current = items;
  const sig = (items || []).map((i) => i && `${i.key}:${i.title}`).join('|');
  useEffect(() => {
    const own = (ref.current || []).filter(Boolean).map((i) => ({ ...i, onClick: (...a) => ref.current.find((x) => x?.key === i.key)?.onClick?.(...a) }));
    screenItems = [...screenItems.filter((i) => !own.some((o) => o.key === i.key)), ...own];
    emit();
    return () => {
      screenItems = screenItems.filter((i) => !own.includes(i));
      emit();
    };
  }, [sig]);
}

export function useScreenSettingsList() {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

/* ── recently opened ── */
const RECENT_KEY = 'bchinot-next-related-recent';

export function readRecent() {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch { return []; }
}

export function pushRecent(id) {
  try {
    const next = [id, ...readRecent().filter((x) => x !== id)].slice(0, 4);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch { /* ignore */ }
}

/* ── "חזרה ל…" after jumping from a screen to its setting ── */
const RETURN_KEY = 'bchinot-related-return';

export function readReturn() {
  try { return JSON.parse(sessionStorage.getItem(RETURN_KEY)) || null; } catch { return null; }
}

export function writeReturn(value) {
  try {
    if (value) sessionStorage.setItem(RETURN_KEY, JSON.stringify(value));
    else sessionStorage.removeItem(RETURN_KEY);
  } catch { /* ignore */ }
}
